// HUD layout (DESIGN.md §4): shift lights top centre, lap/sectors/delta top
// left, mini-map top right, speed + gear bottom centre with the steering arc.
// Bottom-left/right corners stay empty for the throttle/brake thumbs.

import type { Track } from '../track/track';

const SHIFT_LEDS = 15;

export interface HudState {
  speedKmh: number;
  gear: number | string;
  shiftLight: number;
  steer: number;
  steerVisible: boolean;
  lapTime: number | undefined;
  lastLap: number | undefined;
  bestLap: number | undefined;
  delta: number | undefined;
  lapValid: boolean;
  sectors: Array<number | undefined>;
  bestSectors: Array<number | undefined>;
  straightMode: boolean;
  carX: number;
  carZ: number;
  heading: number;
  /** Energy store charge 0..1 (visible battery meter, v2). */
  battery: number;
  /** MGU-K deploying (+) or harvesting (−). */
  deploying: number;
}

export function formatTime(t: number | undefined): string {
  if (t === undefined) return '-:--.---';
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return `${m}:${s.toFixed(3).padStart(6, '0')}`;
}

export class Hud {
  private readonly leds: HTMLElement[] = [];
  private readonly speed: HTMLElement;
  private readonly gear: HTMLElement;
  private readonly lap: HTMLElement;
  private readonly delta: HTMLElement;
  private readonly best: HTMLElement;
  private readonly sectors: HTMLElement[] = [];
  private readonly arc: SVGPathElement;
  private readonly arcWrap: HTMLElement;
  private readonly aero: HTMLElement;
  private readonly notice: HTMLElement;
  private readonly battery: HTMLElement;
  private readonly map: CanvasRenderingContext2D;
  private readonly mapCanvas: HTMLCanvasElement;
  private mapBase: ImageData | undefined;
  private mapTransform = { scale: 1, ox: 0, oz: 0 };
  private noticeUntil = 0;

  constructor(
    private readonly root: HTMLElement,
    private readonly track: Track,
    sectorCount: number,
  ) {
    root.innerHTML = `
      <div class="hud-shift"></div>
      <div class="hud-timing" role="button" aria-label="Pause">
        <div class="hud-pause">❚❚</div>
        <div class="hud-lap">-:--.---</div>
        <div class="hud-delta"></div>
        <div class="hud-sectors"></div>
        <div class="hud-best">Best -:--.---</div>
      </div>
      <canvas class="hud-map" width="220" height="160"></canvas>
      <div class="hud-aero">STRAIGHT MODE</div>
      <div class="hud-notice"></div>
      <button class="hud-speedo" aria-label="Tap to re-centre steering">
        <svg class="hud-arc" viewBox="-60 -60 120 70"><path d="M -50 0 A 50 50 0 0 1 50 0" class="hud-arc-bg"/><path class="hud-arc-fg"/></svg>
        <div class="hud-speed">0</div>
        <div class="hud-gear">N</div>
        <div class="hud-battery"><i></i></div>
        <div class="hud-recentre">tap to re-centre</div>
      </button>
      <div class="hud-pedals" aria-hidden="true">
        <div data-p="clutch"><i></i></div><div data-p="brake"><i></i></div><div data-p="throttle"><i></i></div>
      </div>`;
    const q = <T extends Element>(sel: string): T => root.querySelector(sel) as T;
    const shift = q<HTMLElement>('.hud-shift');
    for (let i = 0; i < SHIFT_LEDS; i++) {
      const led = document.createElement('span');
      led.className = 'led ' + (i < 5 ? 'g' : i < 10 ? 'r' : 'b');
      shift.appendChild(led);
      this.leds.push(led);
    }
    this.speed = q('.hud-speed');
    this.gear = q('.hud-gear');
    this.lap = q('.hud-lap');
    this.delta = q('.hud-delta');
    this.best = q('.hud-best');
    this.aero = q('.hud-aero');
    this.notice = q('.hud-notice');
    this.battery = q('.hud-battery');
    this.arc = q<SVGPathElement>('.hud-arc-fg');
    this.arcWrap = q('.hud-arc');
    const sectorsEl = q<HTMLElement>('.hud-sectors');
    for (let i = 0; i < sectorCount; i++) {
      const el = document.createElement('span');
      el.className = 'sector';
      el.textContent = `S${i + 1}`;
      sectorsEl.appendChild(el);
      this.sectors.push(el);
    }
    this.mapCanvas = q('.hud-map');
    this.map = this.mapCanvas.getContext('2d')!;
    this.drawMapBase();
  }

  get mapElement(): HTMLElement {
    return this.mapCanvas;
  }

  get timingPanel(): HTMLElement {
    return this.lap.parentElement!;
  }

  /**
   * Wheel mode: no re-centring (a wheel has a real centre); show the pedal
   * inputs beside the speedo instead. `undefined` turns it off.
   */
  setPedals(p: { throttle: number; brake: number; clutch: number } | undefined): void {
    this.root.classList.toggle('wheel-mode', !!p);
    if (!p) return;
    for (const k of ['throttle', 'brake', 'clutch'] as const) {
      (this.root.querySelector(`.hud-pedals [data-p="${k}"] i`) as HTMLElement).style.height = `${Math.round(p[k] * 100)}%`;
    }
  }

  get recentreButton(): HTMLElement {
    return this.speed.parentElement!;
  }

  /** Straight Mode pill text ("FRONT WING" when only partial activation is allowed). */
  setAeroLabel(text: string): void {
    if (this.aero.textContent !== text) this.aero.textContent = text;
  }

  showNotice(text: string, seconds = 2): void {
    this.notice.textContent = text;
    this.notice.classList.add('show');
    this.noticeUntil = performance.now() + seconds * 1000;
  }

  private drawMapBase(): void {
    const pts = this.track.points;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const p of pts) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minZ = Math.min(minZ, p.z);
      maxZ = Math.max(maxZ, p.z);
    }
    const { width, height } = this.mapCanvas;
    const pad = 12;
    const scale = Math.min((width - 2 * pad) / (maxX - minX), (height - 2 * pad) / (maxZ - minZ));
    // North up: canvas y grows downwards, so flip z.
    this.mapTransform = { scale, ox: (width - (maxX - minX) * scale) / 2 - minX * scale, oz: (height + (maxZ - minZ) * scale) / 2 + minZ * scale };
    const ctx = this.map;
    ctx.clearRect(0, 0, width, height);
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    pts.forEach((p, i) => {
      const [x, y] = this.toMap(p.x, p.z);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.stroke();
    this.mapBase = ctx.getImageData(0, 0, width, height);
  }

  private toMap(x: number, z: number): [number, number] {
    const t = this.mapTransform;
    return [t.ox + x * t.scale, t.oz - z * t.scale];
  }

  update(s: HudState): void {
    const lit = Math.round(s.shiftLight * SHIFT_LEDS);
    const flash = s.shiftLight >= 1 && Math.floor(performance.now() / 80) % 2 === 0;
    this.leds.forEach((led, i) => led.classList.toggle('on', flash ? false : i < lit));

    this.speed.textContent = Math.round(s.speedKmh).toString();
    this.gear.textContent = s.gear.toString();
    this.lap.textContent = formatTime(s.lapTime);
    this.lap.classList.toggle('invalid', !s.lapValid);
    this.best.textContent = `Best ${formatTime(s.bestLap)}`;
    if (s.delta === undefined) {
      this.delta.textContent = '';
    } else {
      this.delta.textContent = `${s.delta >= 0 ? '+' : '−'}${Math.abs(s.delta).toFixed(2)}`;
      this.delta.className = 'hud-delta ' + (s.delta <= 0 ? 'faster' : 'slower');
    }
    this.sectors.forEach((el, i) => {
      const t = s.sectors[i];
      const best = s.bestSectors[i];
      el.classList.toggle('done', t !== undefined);
      el.classList.toggle('pb', t !== undefined && best !== undefined && t <= best);
    });
    this.aero.classList.toggle('show', s.straightMode);
    const bar = this.battery.firstElementChild as HTMLElement;
    bar.style.width = `${Math.round(Math.max(0, Math.min(1, s.battery)) * 100)}%`;
    this.battery.classList.toggle('deploy', s.deploying > 1000);
    this.battery.classList.toggle('harvest', s.deploying < -1000);

    // Steering arc: sweeps from the top centre towards the side being steered.
    this.arcWrap.style.opacity = s.steerVisible ? '1' : '0';
    const ang = Math.max(-1, Math.min(1, s.steer)) * (Math.PI / 2);
    const ex = 50 * Math.sin(ang);
    const ey = -50 * Math.cos(ang);
    this.arc.setAttribute('d', `M 0 -50 A 50 50 0 0 ${ang >= 0 ? 1 : 0} ${ex.toFixed(2)} ${ey.toFixed(2)}`);

    if (this.noticeUntil && performance.now() > this.noticeUntil) {
      this.notice.classList.remove('show');
      this.noticeUntil = 0;
    }

    if (this.mapBase) {
      this.map.putImageData(this.mapBase, 0, 0);
      const [x, y] = this.toMap(s.carX, s.carZ);
      this.map.fillStyle = '#ffcc33';
      this.map.beginPath();
      this.map.arc(x, y, 5, 0, Math.PI * 2);
      this.map.fill();
    }
  }
}
